import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subMonths, startOfMonth, parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { IAHeader } from '@/components/indicadores-ambientais/IAHeader';
import { IAFilters } from '@/components/indicadores-ambientais/IAFilters';
import { Co2HeroCard } from '@/components/indicadores-ambientais/Co2HeroCard';
import { TotalColetadoCard } from '@/components/indicadores-ambientais/TotalColetadoCard';
import { WasteResumoCard } from '@/components/indicadores-ambientais/WasteResumoCard';
import { IAEvolutionChart } from '@/components/indicadores-ambientais/IAEvolutionChart';
import { IADistribuicaoChart } from '@/components/indicadores-ambientais/IADistribuicaoChart';
import { IAUnitRanking } from '@/components/indicadores-ambientais/IAUnitRanking';
import { IAFAQ } from '@/components/indicadores-ambientais/IAFAQ';
import { Tabs, TabsContent, TabsList, TabsTrigger, Text, cn } from '@musa.tech/design-system';
import { Clock } from 'lucide-react';
import { openCloudHumansChat } from '@/utils/cloudHumans';
import { useEnvironmentalData } from '@/hooks/useEnvironmentalData';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskEstablishmentName, maskNumericValue, maskPercentage, maskMaterialTotals, maskRankingData } from '@/utils/demoMode';
import { getGroupConfig, compareGroups } from '@/utils/materialGroups';
import { fetchEnvironmentalLastAvailableMonth } from '@/integrations/agreements/client';
import { isInternal } from '@/utils/keycloakRoles';
import { CertificadosTab } from '@/components/indicadores-ambientais/CertificadosTab';
import { usePlantonCertificates } from '@/hooks/usePlantonCertificates';

function defaultMonthStr(): string {
  return format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM');
}

const IndicadoresAmbientais = () => {
  const { isDemoMode } = useDemoMode();
  const plantonCerts = usePlantonCertificates();
  const { data: lastAvailableMonth, isLoading: isLoadingLastMonth } = useQuery({
    queryKey: ['environmental-last-available-month'],
    queryFn: fetchEnvironmentalLastAvailableMonth,
    staleTime: 10 * 60 * 1000,
  });

  const hasAnyData = isLoadingLastMonth || lastAvailableMonth !== null;

  const [selectedMonths, setSelectedMonths] = useState<string[]>([defaultMonthStr()]);
  const [establishments, setEstablishments] = useState<string[]>([]);
  const [generators, setGenerators] = useState<string[]>([]);

  useEffect(() => {
    if (lastAvailableMonth) {
      setSelectedMonths([lastAvailableMonth]);
    }
  }, [lastAvailableMonth]);

  const defaultMonth = lastAvailableMonth ?? defaultMonthStr();

  const hasActiveFilters =
    selectedMonths.length !== 1 ||
    selectedMonths[0] !== defaultMonth ||
    establishments.length > 0 ||
    generators.length > 0;

  const handleClear = () => {
    setSelectedMonths([defaultMonth]);
    setEstablishments([]);
    setGenerators([]);
  };

  const {
    materialTotals,
    co2Totals,
    co2Evolution,
    unitRanking,
    isLoading: envLoading,
    error: envError,
  } = useEnvironmentalData({ months: selectedMonths, establishments, generators });

  const evolutionData = co2Evolution
    .filter((e) => e.month >= '2026-01')
    .map((e) => ({
      period: format(parseISO(`${e.month}-01`), 'MMM', { locale: ptBR }),
      co2: e.carbon_saved,
    }));

  const maskedMaterialTotals = maskMaterialTotals(materialTotals, isDemoMode);

  const distribuicaoData = (() => {
    const map = new Map<string, { weight: number; carbon_saved: number; color: string; breakdown: { name: string; weight: number }[] }>();
    for (const m of maskedMaterialTotals) {
      const existing = map.get(m.group);
      if (existing) {
        existing.weight += m.weight_collected;
        existing.carbon_saved += m.carbon_saved;
        existing.breakdown.push({ name: m.material.name, weight: m.weight_collected });
      } else {
        map.set(m.group, {
          weight: m.weight_collected,
          carbon_saved: m.carbon_saved,
          color: getGroupConfig(m.group).color,
          breakdown: [{ name: m.material.name, weight: m.weight_collected }],
        });
      }
    }
    return Array.from(map.entries())
      .filter(([, data]) => data.carbon_saved > 0)
      .sort(([, dataA], [, dataB]) => dataB.weight - dataA.weight)
      .map(([group, data]) => ({
        group,
        ...data,
        breakdown: data.breakdown.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
      }));
  })();

  const rankingData = (() => {
    const map = new Map<number, {
      unit: string;
      co2: number;
      breakdown: { group: string; co2: number; materials: { name: string; co2: number }[] }[];
    }>();

    for (const u of unitRanking) {
      const est = map.get(u.establishment.id);
      if (est) {
        est.co2 += u.carbon_saved;
        const grp = est.breakdown.find((b) => b.group === u.group);
        if (grp) {
          grp.co2 += u.carbon_saved;
          grp.materials.push({ name: u.material.name, co2: u.carbon_saved });
        } else {
          est.breakdown.push({ group: u.group, co2: u.carbon_saved, materials: [{ name: u.material.name, co2: u.carbon_saved }] });
        }
      } else {
        map.set(u.establishment.id, {
          unit: maskEstablishmentName(u.establishment.name, isDemoMode),
          co2: u.carbon_saved,
          breakdown: [{ group: u.group, co2: u.carbon_saved, materials: [{ name: u.material.name, co2: u.carbon_saved }] }],
        });
      }
    }

    return Array.from(map.values()).map(({ unit, co2, breakdown }) => ({
      unit,
      value: co2,
      co2,
      breakdown: breakdown
        .filter((g) => g.co2 > 0)
        .sort((a, b) => compareGroups(a.group, b.group))
        .map((g) => ({
          ...g,
          materials: g.materials.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
        })),
    }));
  })();

  return (
      <div className="min-h-screen bg-background p-4 md:p-6 w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 min-w-0">
          <IAHeader />

          <Tabs defaultValue="indicadores">
            {!isDemoMode && (isInternal() || plantonCerts.certificates.length > 0) && (
              <TabsList>
                <TabsTrigger value="indicadores">Indicadores</TabsTrigger>
                <TabsTrigger value="certificados">Certificados</TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="indicadores" className="space-y-4 md:space-y-6 mt-4">
              {hasAnyData ? (
                <>
                  <div className={cn('flex flex-col', hasActiveFilters ? 'gap-4' : 'gap-2')}>
                    <IAFilters
                      months={selectedMonths}
                      defaultMonth={defaultMonth}
                      maxMonth={lastAvailableMonth ?? undefined}
                      establishments={establishments}
                      generators={generators}
                      onMonthsChange={setSelectedMonths}
                      onEstablishmentsChange={setEstablishments}
                      onGeneratorsChange={setGenerators}
                      onClear={handleClear}
                    />
                  </div>

                  <Co2HeroCard
                    value={maskNumericValue(co2Totals?.carbon_saved ?? 0, isDemoMode)}
                    change={co2Totals?.variation_percent != null ? maskPercentage(co2Totals.variation_percent, isDemoMode) : null}
                    isLoading={envLoading}
                  />

                  <TotalColetadoCard
                    value={maskNumericValue(co2Totals?.weight_collected ?? 0, isDemoMode)}
                    change={null}
                    isLoading={envLoading}
                  />

                  <WasteResumoCard
                    materialTotals={maskedMaterialTotals}
                    isLoading={envLoading}
                    error={envError}
                  />

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <IAEvolutionChart
                      data={isDemoMode ? evolutionData.map((e, i) => ({ ...e, co2: (i + 1) * 1000 })) : evolutionData}
                      isLoading={envLoading}
                      error={envError}
                    />
                    <IADistribuicaoChart
                      data={distribuicaoData}
                      isLoading={envLoading}
                      error={envError}
                    />
                  </div>

                  {(envLoading || rankingData.length > 1) && (
                    <IAUnitRanking
                      data={maskRankingData(rankingData, isDemoMode)}
                      isLoading={envLoading}
                      error={envError}
                    />
                  )}

                  <IAFAQ />

                  <Text
                    variant="body"
                    size="xsmall"
                    className="text-center text-neutral-500 leading-relaxed px-4 py-2 bg-neutral-100 rounded-lg border border-neutral-200 block"
                  >
                    Os cálculos consideram exclusivamente resíduos recicláveis: metais, plásticos, papel,
                    vidro e recicláveis. Os dados são baseados em estimativas a partir de fatores de conversão
                    reconhecidos internacionalmente (GHG Protocol, EPA, IPCC). Os valores reais podem variar
                    conforme condições específicas de processamento e destinação. Este relatório não substitui
                    auditorias ambientais formais.
                  </Text>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Clock className="w-16 h-16 text-[#10261F]/30 mb-6" />
                  <h2 className="text-xl font-semibold text-[#10261F] mb-2">Página em construção</h2>
                  <p className="text-gray-500 max-w-sm">
                    Caso precise de ajuda,{' '}
                    <button
                      type="button"
                      onClick={openCloudHumansChat}
                      className="text-primary-600 hover:underline font-medium"
                    >
                      entre em contato com nosso suporte
                    </button>
                    .
                  </p>
                </div>
              )}
            </TabsContent>

            {!isDemoMode && (
              <TabsContent value="certificados" className="mt-4">
                <CertificadosTab />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
  );
};

export default IndicadoresAmbientais;
